'use client';

import { type FileRejection } from '@curology/ui-components-web';
import { Accordion } from '@curology/ui-components-web/react/Accordion';
import { AccordionContent } from '@curology/ui-components-web/react/Accordion/AccordionContent';
import { AccordionSummary } from '@curology/ui-components-web/react/Accordion/AccordionSummary';
import { Button } from '@curology/ui-components-web/react/Button';
import { ImageUpload } from '@curology/ui-components-web/react/FileUpload';
import CameraIcon from '@curology/ui-components-web/react/Icon/icons/CameraIcon';
import TrashIcon from '@curology/ui-components-web/react/Icon/icons/TrashIcon';
import { Link } from '@curology/ui-components-web/react/Link';
import { Typography } from '@curology/ui-components-web/react/Typography';
import Image from 'next/image';
import React, { useState } from 'react';

import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import { useNavigation } from '@/components/flow/hooks/useNavigation';
import { PhotoIdModal } from '@/components/flow/steps/survey/surveyjsSurvey/surveyjs/components/photoUpload/components/photoIdModal';
import { BRAND_IDS } from '@/consts/brands';
import { DATA_KEYS } from '@/consts/dataKeys';
import { TreatmentCategoryIds } from '@/consts/treatmentCategories';
import { useVisitContext } from '@/services/visitsService/hooks/useVisitContext';
import { trackEvent, AnalyticsEvent } from '@/utils/analytics';
import { acceptMediaAndroid } from '@/utils/images/acceptMediaAndroid';

import { PHOTO_ID_METADATA } from './consts/consts';
import { type UploadedPhoto } from './types/types';
import {
  getLatestPhotoIdUploadedPhoto,
  getPageTitleAndDescription,
  deleteUploadedPhoto,
  uploadPhoto,
  fileRejected,
} from './utils/utils';
import { useDataProvider } from '../../components/DataProvider/useDataProvider';

export const PhotoIdUpload = () => {
  const providedData = useDataProvider(
    DATA_KEYS.basicPatient,
    DATA_KEYS.photoUploadedData,
  );
  const patient = providedData.basicPatient;
  const uploadedPhotos = providedData.photoUploadedData?.photos || [];
  const { treatmentCategory, brand } = useFlowContext();
  const { visit } = useVisitContext();
  const brandId = BRAND_IDS[brand];
  const { onNext, onBack } = useNavigation();

  const latestPhotoIdUploadedPhoto = getLatestPhotoIdUploadedPhoto(
    uploadedPhotos,
    treatmentCategory,
  );

  const [accordionOpen, setAccordionOpen] = useState(true);
  const [allInputsDisabled, setAllInputsDisabled] = useState(false);
  const [error, setError] = React.useState<Error | undefined>();
  const [showPhotoIdModal, setShowPhotoIdModal] = useState(false);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(
    !latestPhotoIdUploadedPhoto,
  );
  const [uploadedPhoto, setUploadedPhoto] = useState<UploadedPhoto | undefined>(
    latestPhotoIdUploadedPhoto,
  );
  const [uploading, setUploading] = useState(false);

  const { pageDescription, pageTitle } = getPageTitleAndDescription(
    patient?.isMinor,
  );

  const aspectRatio =
    PHOTO_ID_METADATA.imagesWidth / PHOTO_ID_METADATA.imagesHeight;

  /**
   * Override the file input's `accept` parameter for Android so we offer the user
   * the choice of file picker or camera to the user. Should use the Dropzone default for
   * all platforms expect Android, which will use the non-standard: `accept="image/*;capture=camera"`
   * See this gist for more details: https://gist.github.com/danawoodman/4788404bc620d5392d111dba98c73873
   */
  const acceptAttribute = acceptMediaAndroid(navigator);

  return (
    <div className="cur-max-w-[375px] cur-mx-auto cur-px-4 cur-py-8">
      <Typography className="cur-mb-4 cur-text-primary" size="h2">
        {pageTitle}
      </Typography>
      <Accordion
        className="cur-bg-surface-neutral cur-my-4"
        open={accordionOpen}
      >
        <AccordionSummary
          className="cur-border-t-0 cur-p-2"
          onClick={() => setAccordionOpen(!accordionOpen)}
        >
          <div className="cur-flex cur-items-center cur-justify-between">
            <Typography data-testid="photo-accordion-title" weight="bold">
              {PHOTO_ID_METADATA.title}
            </Typography>
            <Typography
              data-testid="photo-accordion-required-or-optional"
              size="meta"
            >
              Required
            </Typography>
          </div>
        </AccordionSummary>
        <AccordionContent className="cur-border-solid cur-border-t cur-border-t-curology-primary cur-m-0 cur-p-4">
          <Typography
            className="cur-mb-4"
            data-testid="photo-accordion-description"
          >
            {pageDescription}
          </Typography>
          <div className="cur-flex">
            <div className="cur-flex-1 cur-text-center">
              <div className="cur-pr-2">
                <Typography className="cur-mb-2" weight="bold">
                  Example
                </Typography>
                <div className="cur-mt-2 cur-relative" style={{ aspectRatio }}>
                  <Image
                    alt={PHOTO_ID_METADATA.exampleImageAlt}
                    fill={true}
                    src={PHOTO_ID_METADATA.exampleImageUrl}
                  />
                </div>
              </div>
            </div>
            <div className="cur-flex-1 cur-text-center">
              <div className="cur-pl-2">
                <Typography className="cur-mb-2" weight="bold">
                  Your Photo
                </Typography>
                <div className="cur-flex cur-flex-col cur-mt-2">
                  <div>
                    <ImageUpload
                      aspectRatio="auto"
                      className="cur-box-border cur-h-full"
                      {...acceptAttribute}
                      complete={!!uploadedPhoto}
                      completeAction={
                        <Button
                          buttonType="text"
                          disabled={allInputsDisabled}
                          icon={<TrashIcon />}
                          onClick={() =>
                            deleteUploadedPhoto(
                              brand,
                              setSubmitButtonDisabled,
                              setUploadedPhoto,
                              uploadedPhoto,
                            )
                          }
                          size="sm"
                          title="Delete?"
                        />
                      }
                      data-testid="photo-id-uploader-image-to-upload"
                      disabled={allInputsDisabled}
                      error={error}
                      icon={<CameraIcon />}
                      iconSize="md"
                      initialLabel={PHOTO_ID_METADATA.uploadImageText}
                      onFileAdded={(files: File[]) =>
                        uploadPhoto(
                          brand,
                          files,
                          setSubmitButtonDisabled,
                          setUploadedPhoto,
                          setUploading,
                          treatmentCategory,
                          setError,
                          visit?.id,
                        )
                      }
                      onFileRejected={(fileRejections: FileRejection[]) =>
                        fileRejected(fileRejections, setError)
                      }
                      src={uploadedPhoto?.localUrl}
                      style={{
                        aspectRatio,
                      }}
                      uploading={uploading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="cur-text-center cur-mt-4">
            <Link
              data-testid="photo-accordion-modal-link"
              href=""
              onClick={(event) => {
                setShowPhotoIdModal(true);
                event.preventDefault();
              }}
              size="cta"
            >
              {PHOTO_ID_METADATA.modalLinkText}
            </Link>
          </div>
        </AccordionContent>
      </Accordion>
      <div className="cur-my-4">
        <Button
          className="cur-w-full"
          disabled={allInputsDisabled || submitButtonDisabled}
          onClick={() => {
            setAllInputsDisabled(true);

            trackEvent(AnalyticsEvent.PHOTO_ID_UPLOAD_COMPLETED, {
              brandId,
              treatmentCategory: treatmentCategory,
              treatmentCategoryId: TreatmentCategoryIds[treatmentCategory],
              userId: patient?.id,
            });

            onNext();
          }}
        >
          Send photo ID to provider
        </Button>
      </div>
      <div className="cur-mt-4">
        <Button
          buttonType="text"
          className="cur-text-primary cur-w-full"
          disabled={allInputsDisabled}
          onClick={() => {
            setAllInputsDisabled(true);

            onBack();
          }}
        >
          Back
        </Button>
      </div>
      <PhotoIdModal
        setShowPhotoIdModal={setShowPhotoIdModal}
        showPhotoIdModal={showPhotoIdModal}
      />
    </div>
  );
};
